import FormatService from "services/formatService";

/**
 * Builds the pricing matrix from API data for the "by activity" view.
 * Rows are clients, columns are lots.
 * Returns { rows, lotIdsFromData }.
 */
export const buildActivityPricingMatrixData = ({
    activityPricing,
    selectedLotIds,
    clientLookup,
    vendorLookup,
}) => {
    const hasLotFilter = selectedLotIds?.length > 0;
    const clientMap = {};
    const lotIdsFromData = new Set();

    // Seed a row for every tenant client so clients without pricing still
    // appear. This is what lets the matrix render a full client x lot grid of
    // empty (clickable) cells even when the API returns no pricing for the
    // selected criteria — the user adds pricing by clicking an empty cell.
    Object.keys(clientLookup || {}).forEach((clientKey) => {
        clientMap[clientKey] = {
            id: Number(clientKey),
            clientId: Number(clientKey),
            clientLabel: clientLookup[clientKey]?.name ?? String(clientKey),
        };
    });

    // activityPricing may be undefined while the query is in flight; the table
    // is hidden during the initial load, so just skip cell population here.
    if (Array.isArray(activityPricing)) {
        activityPricing.forEach((pricing) => {
            if (!pricing || pricing.lotId === undefined || pricing.clientId === undefined) {
                return;
            }

            if (hasLotFilter && !selectedLotIds.includes(pricing.lotId)) {
                return;
            }

            lotIdsFromData.add(pricing.lotId);

            const clientKey = pricing.clientId;
            const lotKey = `lot_${pricing.lotId}`;
            if (clientMap[clientKey] && !clientMap[clientKey][lotKey]) {
                clientMap[clientKey][lotKey] = {
                    ...pricing,
                    formattedCharge: FormatService.formatCurrency(pricing.charge),
                    isPMFVendor: vendorLookup?.[pricing.preferredVendorId]?.name === "PMF",
                    priceVariance:
                        pricing.priceVarianceData?.sameClientOtherLots?.length > 0 ||
                        pricing.priceVarianceData?.otherClientsSameLot?.length > 0,
                    margin: pricing.margin ?? null,
                };
            }
        });
    }

    const rows = Object.values(clientMap).sort((a, b) =>
        a.clientLabel.localeCompare(b.clientLabel)
    );

    return { rows, lotIdsFromData };
};
